import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { GetStaticProps } from "next";
import {
  ContentfulHeaderProps,
  getContentfulHeaderProps,
} from "../../contentful/getContentfulHeader";
import { withTranslationAndContentfulStaticProps } from "../../contentful/withTranslationAndContentfulStaticProps";
import { LayoutPage } from "components/PageTemplates/Layout";
import { MapProvider } from "../../components/Maps/MapProvider";
import MapComponent, {
  MarkerType,
  PlaceDetails,
  VetMapRef,
} from "components/Maps/VetMap";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import styles from "./index.module.css";
import Overlay from "components/Signup/components/Overlay";
import { isOrg } from "utils/site";

import vetClinicDataJSON from "../../data/clinic_avg_cost.json";
import vetClinicsJSON from "../../data/clinic_list.json";
import { ClinicDetails } from "components/Maps/ClinicDetails";
import { ClinicList } from "components/Maps/ClinicList";

type BillableData = {
  median_amount: number;
  distinct_values: number;
  group: string;
};

export type ClinicData = {
  clinic: string;
  address: string;
  median_amount: number;
  distinct_values: number;
  group: string;
  opening_hours: string;
  billables: {
    [key: string]: BillableData;
  };
};

type OpeningHour = {
  day: string;
  open: string;
  close: string;
};

/**
 * Parse the opening hours from the JSON file generated by Oskar's script (based on Google API)
 * @param hours - The opening hours from the JSON file
 * @returns The opening hours in the format of OpeningHour[]
 */
const parseOpeningHours = (hours: string): OpeningHour[] => {
  if (!hours) return [];

  try {
    // First remove the outer brackets
    const withoutBrackets = hours.slice(1, -1);

    // Split by '], [' while preserving quoted strings with commas inside them
    const hoursArray =
      withoutBrackets
        .split("', '")
        .map((s) => s.replace(/^'|'$/g, "").trim()) || [];

    return hoursArray
      .map((hour) => {
        if (!hour) {
          console.warn("Empty hour string encountered");
          return [];
        }

        // Handle "Stängt" (Closed) case
        if (hour.includes("Stängt")) {
          const day = hour.split(":")[0];
          return {
            day,
            open: "closed",
            close: "closed",
          };
        }

        if (hour.includes("Öppet dygnet runt")) {
          const day = hour.split(":")[0];
          return {
            day,
            open: "00:00",
            close: "00:00",
          };
        }

        const [day, times] = hour.split(": ");
        if (!times) {
          console.warn("No times found for day:", day, "in hour string:", hour);
          return [];
        }

        const timeSlots = times.split(", ");

        // Get earliest opening and latest closing time
        const openTimes = timeSlots
          .map((slot) => {
            const [open] = slot.split("–");
            return open;
          })
          .filter(Boolean);

        const closeTimes = timeSlots
          .map((slot) => {
            const [, close] = slot.split("–");
            return close;
          })
          .filter(Boolean);

        if (!openTimes.length || !closeTimes.length) {
          console.warn(
            "No valid times found for day:",
            day,
            "in hour string:",
            hour
          );
          return [];
        }

        return {
          day,
          open: openTimes.sort()[0], // earliest opening time
          close: closeTimes.sort().reverse()[0], // latest closing time
        };
      })
      .filter((entry): entry is OpeningHour => entry !== null);
  } catch (error) {
    console.error("Error parsing opening hours:", error, "Input:", hours);
    return [];
  }
};

const Disclaimer = () => {
  return (
    <FlexRow justifyContent="center">
      <div className={styles.disclaimer}>
        <Typography variant="body2" color="grey400">
          Priserna som visas är genomsnittliga kostnader för skador registrerade
          hos Lassie under de senaste två åren. Observera att det faktiska
          priset hos veterinären kan variera beroende på de individuella
          förutsättningar som gäller för ditt besök och ditt djur.
        </Typography>
      </div>
    </FlexRow>
  );
};

export type VetClinicMarker = MarkerType & Partial<ClinicData>;

function VetMap({
  navigationItems,
  siteLanguages,
  clinicList,
  vetData,
  nationalData,
}: ContentfulHeaderProps & {
  clinicList: {
    id: string;
    placeId: string;
    name: string;
    lat: number;
    lng: number;
    alwaysOpen: boolean;
    address: string;
    openingHours: OpeningHour[];
  }[];
  vetData: ClinicData[];
  nationalData: ClinicData;
}) {
  const mapRef = useRef<VetMapRef>(null);

  const [markers, setMarkers] = useState<VetClinicMarker[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetails | null>(null);
  const [overlayDisplay, setOverlayDisplay] = useState<{
    title: string;
    content: ReactNode;
    full?: boolean;
  } | null>(null);

  const clinicsWithPrices = useMemo(() => {
    const map = new Map();
    vetData.forEach((clinic: ClinicData) => {
      map.set(clinic.address, clinic);
    });
    return map;
  }, [vetData]);

  const predefinedClinics = useMemo(() => {
    return clinicList.map((clinic) => {
      const clinicData = clinicsWithPrices.get(clinic.address);
      return {
        id: clinic.id,
        placeId: "", // full clinic list has no google place id in json
        name: clinic.name,
        lat: clinic.lat,
        lng: clinic.lng,
        alwaysOpen: clinic.alwaysOpen,
        address: clinic.address,
        openingHours: clinicData?.opening_hours
          ? parseOpeningHours(clinicData?.opening_hours)
          : clinic.openingHours,
      };
    });
  }, [clinicList, clinicsWithPrices]);

  const markersWithData = useMemo(() => {
    if (markers.length > 0) {
      return markers.map((marker) => {
        const data = clinicsWithPrices.get(marker.address);
        if (data && !marker.median_amount) {
          return {
            ...marker,
            ...data,
          };
        }
        return marker;
      });
    }
    return markers;
  }, [markers, clinicsWithPrices]);

  const onSelectPlace = useCallback((place: PlaceDetails) => {
    setSelectedPlace(place);
  }, []);

  const onMarkersChange = useCallback((markers: MarkerType[]) => {
    setMarkers(markers);
  }, []);

  const onListItemClick = (item: VetClinicMarker) => {
    mapRef.current?.selectMarkerById(item.id);
    const element = document.getElementById("vetmap");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const selectedPlaceMarker = markersWithData.find(
    (marker) => marker.id === selectedPlace?.id
  ) as VetClinicMarker;

  return (
    <MapProvider>
      <LayoutPage
        title="Veterinärkartan"
        navigationItems={navigationItems}
        siteLanguages={siteLanguages}
        seoNoIndex
        seoNoFollow
      >
        <Gutter size={4} />
        <FlexRow justifyContent="center" className={styles.pageTitle}>
          <Typography variant="h1">Hitta en veterinär nära dig</Typography>
        </FlexRow>
        <Gutter size={4} />
        <FlexRow justifyContent="center">
          <FlexCol className={styles.pageDescription}>
            <Typography variant="body1" color="grey400">
              Veterinärkostnaderna kan varierar mycket från ort till ort och
              mellan kliniker. Veterinärkartan hjälper dig att snabbt få en bild
              av de genomsnittliga veterinärkostnaderna där du bor, så att du
              kan välja rätt veterinärklinik för dig och ditt djur.
            </Typography>
          </FlexCol>
        </FlexRow>
        <Gutter size={4} />
        <MapComponent
          id="vetmap"
          ref={mapRef}
          predefinedLocations={predefinedClinics}
          onSelectPlace={onSelectPlace}
          onMarkersChange={onMarkersChange}
        />
        <Gutter size={2} />

        <FlexRow className={styles.container}>
          {markersWithData.length === 0 && (
            <FlexCol alignItems="center">
              <Typography variant="h3">
                Hittade inga veterinärer i närheten — prova ett annat område!
              </Typography>
              <Gutter size={2} />
              <Typography variant="body1" color="grey400">
                Testa att zooma ut eller flytta runt på kartan.
              </Typography>
            </FlexCol>
          )}
          <ClinicList
            markers={markersWithData}
            selectedClinicId={selectedPlaceMarker?.id ?? null}
            onClinicPress={onListItemClick}
          />
          {selectedPlace && (
            <div className={styles.clinicDetailsContainer}>
              <ClinicDetails
                selectedPlace={selectedPlace}
                selectedPlaceMarker={selectedPlaceMarker}
                nationalData={nationalData}
                setOverlayDisplay={setOverlayDisplay}
              />
            </div>
          )}
        </FlexRow>
        <Gutter size={4} />
        <FlexRow justifyContent="center">
          <Disclaimer />
        </FlexRow>
        <Gutter size={4} />
        <Overlay
          display={overlayDisplay}
          onRemove={() => setOverlayDisplay(null)}
        />
      </LayoutPage>
    </MapProvider>
  );
}

export default VetMap;

export const getStaticProps: GetStaticProps = async (context) => {
  if (!isOrg("swe")) {
    return {
      notFound: true,
    };
  }

  const getContentfulProps = withTranslationAndContentfulStaticProps();
  const headerProps = await getContentfulHeaderProps(context.locale);
  const props = await getContentfulProps(context);
  const vetData = vetClinicDataJSON
    .filter(
      (clinic): clinic is ClinicData =>
        clinic.clinic !== "national" &&
        clinic.clinic !== "Jämtlands Djurklinik AB"
    )
    .map((clinic) => ({
      ...clinic,
      address: clinic.address.toLowerCase(),
    }));
  const national: ClinicData | undefined = vetClinicDataJSON.find(
    (clinic): clinic is ClinicData => clinic.clinic === "national"
  );
  if (national) {
    national.address = national.address.toLowerCase();
  }

  const doubles = vetClinicsJSON.clinics.filter(
    (clinic, index, self) =>
      self.findIndex((t) => t.address === clinic.address) !== index
  );

  if (doubles.length > 0) {
    console.warn("Found multiple clinics with the same address:", doubles);
  }

  const clinicList = vetClinicsJSON.clinics.map((clinic) => ({
    ...clinic,
    address: clinic.address.replace("Sweden", "Sverige").toLowerCase(),
    id: `place-${clinic.address.replaceAll(" ", "_")}`,
    placeId: "", // full clinic list has no google place id in json
  }));

  return {
    props: {
      ...props,
      ...headerProps,
      vetData,
      nationalData: national,
      clinicList,
    },
  };
};
